export const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

// Cost per learner validation constants
export const COST_PER_LEARNER_LIMIT = 1_000_000; // R1000,000.00

export const ORGANOGRAM_FIELDS = [
  {
    field: "dept_id",
    headerName: "Department Id",
  },
  {
    field: "dept_name",
    headerName: "Department Name",
  },
  {
    field: "parent_dept_id",
    headerName: "Parent Department ID",
  },
  {
    field: "dept_head_id",
    headerName: "Department Head ID",
  },
];

/**
 * Example hierarchy for the organogram template (5 rows):
 * Corporate (root) → Engineering & Human Resources; each has one sub-department.
 */
export const ORGANOGRAM_TEMPLATE_SAMPLE = [
  { dept_id: "D001", dept_name: "Corporate", parent_dept_id: "", dept_head_id: "EMP001" },
  { dept_id: "D002", dept_name: "Engineering", parent_dept_id: "D001", dept_head_id: "EMP002" },
  { dept_id: "D003", dept_name: "Human Resources", parent_dept_id: "D001", dept_head_id: "EMP003" },
  { dept_id: "D004", dept_name: "Product Design", parent_dept_id: "D002", dept_head_id: "EMP004" },
  { dept_id: "D005", dept_name: "Learning & Development", parent_dept_id: "D003", dept_head_id: "EMP005" },
];

export const TRAINING_CATALOGUE_FIELDS = [
  // {
  //   field: "training_id",
  //   headerName: "Training ID",
  // },
  {
    field: "training_name",
    headerName: "Training Name",
  },
  {
    field: "program_duration",
    headerName: "Program Duration",
  },
  {
    field: "training_expense",
    headerName: "Cost per Learner",
  },
  {
    field: "training_provider",
    headerName: "Provider",
  },
  {
    field: "training_description",
    headerName: "Training Details",
  },
  {
    field: "training_url",
    headerName: "Training URL",
  },
  {
    field: "targeted_skills",
    headerName: "Targeted Skills",
  },
  {
    field: "nqf_aligned",
    headerName: "NQF Aligned",
  },
  {
    field: "nqf_level",
    headerName: "NQF Level",
  },
];

// Derive required fields from TRAINING_CATALOGUE_FIELDS, excluding NQF Level (conditionally required) and Training URL (optional)
export const TRAINING_CATALOGUE_REQUIRED_FIELDS = TRAINING_CATALOGUE_FIELDS.filter(
  (field) => field.field !== "nqf_level" && field.field !== "training_url"
).map((field) => field.headerName);


export const TRAINING_CATEGORY_OPTIONS = [
  { id: "Category A", value: "Category A" },
  { id: "Category B", value: "Category B" },
  { id: "Category C", value: "Category C" },
  { id: "Category D", value: "Category D" },
  { id: "Category E", value: "Category E" },
  { id: "Category F", value: "Category F" },
  { id: "Category G", value: "Category G" },
];

// Define chat categories and their corresponding labels in a single object for better maintainability
export const CHAT_CATEGORIES = {
  INDUSTRY_TRENDS: {
    id: "industryTrends",
    label: "Industry Trends",
    allowMultiple: true,
  },
  WORKFORCE_IMPACT: {
    id: "workforceImpact",
    label: "Workforce Impact",
    allowMultiple: true,
  },
  TRAINING: {
    id: "training",
    label: "Training & Development",
    allowMultiple: true,
  },
  STRATEGIC_SUMMARY: {
    id: "strategicSummary",
    label: "Strategic Summary",
    allowMultiple: true,
  },
  CRITICAL_SKILLS: {
    id: "criticalSkills",
    label: "Critical Skills",
    allowMultiple: true,
  },
  ALLOCATION_SUMMARY: {
    id: "allocationSummary",
    label: "Allocation Summary",
    allowMultiple: false,
  },
  TRAINING_ALLOCATION: {
    id: "trainingAllocation",
    label: "Training Course Allocation",
    allowMultiple: false,
  },
  DATA_TRANSFORMATION: {
    id: "dataTransformation",
    label: "Data Transformation",
    allowMultiple: false,
  },
  PLAN_OPTIMIZER_DATA_TRANSFORMATION: {
    id: "planOptimizerDataTransformation",
    label: "Plan Optimizer Data Transformation",
    allowMultiple: false,
  },
  EMPLOYEE_DATA_CONVERSION: {
    id: "employeeDataConversion",
    label: "Employee Data Conversion",
    allowMultiple: false,
  },
  TRAINING_PROGRAM_FILTERING: {
    id: "trainingProgramFiltering",
    label: "Training Program Filtering",
    allowMultiple: false,
  },
  STRATEGIC_INPUT_ANALYSIS: {
    id: "strategicInputAnalysis",
    label: "Strategic Input Analysis",
    allowMultiple: false,
  },
  GROUP_REQUEST_LIST: {
    id: "groupRequestList",
    label: "Group Request List",
    allowMultiple: false,
  },
  SYSTEM_PROMPT: {
    id: "systemPrompt",
    label: "System Prompt",
    allowMultiple: false,
  },
};

export const roleType = {
  ADMIN: "admin",
  COMPANY_ADMIN: "company_admin",
  SDF: "sdf",
  EMPLOYEE: "employee",
  MANAGER: "manager",
};

export const COMPANY_PRIVILEGED_ROLES = [
  roleType.COMPANY_ADMIN,
  roleType.SDF,
];

/** Employee-list Role column: labels and styles keyed by roleType values. */
export const EMPLOYEE_ROLE_DISPLAY = {
  [roleType.COMPANY_ADMIN]: { label: "Admin", className: "text-gray-600" },
  [roleType.SDF]: { label: "SDF", className: "text-gray-600" },
  [roleType.MANAGER]: { label: "Manager", className: "text-gray-600" },
  [roleType.EMPLOYEE]: { label: "Employee", className: "text-gray-600" },
};

/** Form history grid / compare dialog role labels. */
export const FORM_HISTORY_ROLE_LABELS = {
  [roleType.ADMIN]: "Admin",
  [roleType.COMPANY_ADMIN]: "Company Admin",
  [roleType.SDF]: "SDF",
  [roleType.MANAGER]: "Manager",
  [roleType.EMPLOYEE]: "Employee",
};

export function formatFormHistoryRole(role) {
  if (!role || role === "-") return "-";
  if (FORM_HISTORY_ROLE_LABELS[role]) return FORM_HISTORY_ROLE_LABELS[role];
  return String(role)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export const jobType = {
  TRAINING: "generate_training_plan",
  SKILLS_PLANNER_ALLOCATION: "skills_planner_allocation",
  EMPLOYEE_IMPORT: "import_employee",
  SEND_INVITE: "send_invite",
};

/** Shown in Generate Training history sidebar (TRAINING + parser saves; not plan optimizer). */
export const generateTrainingPageBatchJobTypes = [
  jobType.TRAINING,
  jobType.SKILLS_PLANNER_ALLOCATION,
];

export const statusType = {
  PROCESSING: "Processing",
  FAILED: "Failed",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

/** User-facing labels for plan_optimizer_scope_snapshot stale categories. */
export const PLAN_OPTIMIZER_STALE_CATEGORY_LABELS = {
  Employee_update: "Employees changed",
  form_update: "Training survey forms changed",
  training_update: "Trainings or allocations changed",
  organogram_update: "Organogram changed",
  strategic_update: "Strategic input changed",
  budget_update: "Budget or configuration changed",
};

export const allocationType = {
  GENDER: "gender",
  RACE: "race",
  EQUAL: "equal",
  CANCELLED: "Cancelled",
};

export const statuses = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  ARCHIVED: "archived",
};

/** Catalogue parent row `training_catalogue.status` is boolean in DB; use for toggles and guards. */
export const isCatalogueAvailable = (category) => category?.status === true;

export const trainingFormStatus = {
  DRAFT: "Draft",
  REQUESTED: "Requested",
  RECOMMENDED: "Recommended",
  APPROVED: "Approved",
  REQUEST_EDIT: "Request Edit",
};

export const trainingFormStatusLabels = {
  Draft: "Saved as Draft",
  Requested: "Request Submitted",
  Recommended: "Recommended",
  Approved: "Approved",
  "Request Edit": "Request Edit",
};

/** Bulk status change options on Manage Training Needs (SDF / company_admin). */
export const BULK_FORM_STATUS_OPTIONS = [
  { value: trainingFormStatus.APPROVED, label: "Approved" },
  { value: trainingFormStatus.RECOMMENDED, label: "Recommended" },
  { value: trainingFormStatus.REQUESTED, label: "Requested" },
  { value: trainingFormStatus.REQUEST_EDIT, label: "Request Edit" },
];

/** Default history comment when a form is submitted for the first time (no user comment). */
export const DEFAULT_FORM_SUBMIT_COMMENT = "Form submitted for approval.";

/** Skills Planner → get_training_forms p_form_status_filter */
export const SKILLS_PLANNER_FORM_STATUS_FILTER = {
  ALL: "all",
  APPROVED: "approved",
  RECOMMENDED: "recommended",
  BOTH: "both",
};

const SKILLS_PLANNER_FORM_STATUS_FILTER_LABELS = {
  [SKILLS_PLANNER_FORM_STATUS_FILTER.ALL]: "All",
  [SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED]: "Approved",
  [SKILLS_PLANNER_FORM_STATUS_FILTER.RECOMMENDED]: "Recommended",
  [SKILLS_PLANNER_FORM_STATUS_FILTER.BOTH]: "Both",
};

/** Dropdown options for Skills Planner status filter (Both excluded from UI). */
export const SKILLS_PLANNER_FORM_STATUS_FILTER_OPTIONS = [
  SKILLS_PLANNER_FORM_STATUS_FILTER.ALL,
  SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED,
  SKILLS_PLANNER_FORM_STATUS_FILTER.RECOMMENDED,
].map((value) => ({
  value,
  label: SKILLS_PLANNER_FORM_STATUS_FILTER_LABELS[value],
}));

export function resolveSkillsPlannerFormStatusFilter(inputData) {
  if (!inputData) return SKILLS_PLANNER_FORM_STATUS_FILTER.ALL;
  if (inputData.form_status_filter) {
    if (inputData.form_status_filter === SKILLS_PLANNER_FORM_STATUS_FILTER.BOTH) {
      return SKILLS_PLANNER_FORM_STATUS_FILTER.ALL;
    }
    return inputData.form_status_filter;
  }
  return inputData.approved_only
    ? SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED
    : SKILLS_PLANNER_FORM_STATUS_FILTER.ALL;
}

export function getTrainingFormsRpcParams(formStatusFilter) {
  return {
    p_form_status_filter: formStatusFilter,
    p_approved_only:
      formStatusFilter === SKILLS_PLANNER_FORM_STATUS_FILTER.APPROVED,
    p_recommended_only:
      formStatusFilter === SKILLS_PLANNER_FORM_STATUS_FILTER.RECOMMENDED,
  };
}

export const NQF_LEVEL_OPTIONS = [
  { id: "Level 1", value: "Level 1" },
  { id: "Level 2", value: "Level 2" },
  { id: "Level 3", value: "Level 3" },
  { id: "Level 4", value: "Level 4" },
  { id: "Level 5", value: "Level 5" },
  { id: "Level 6", value: "Level 6" },
  { id: "Level 7", value: "Level 7" },
  { id: "Level 8", value: "Level 8" },
  { id: "Level 9", value: "Level 9" },
  { id: "Level 10", value: "Level 10" },
];

export const NQF_ALIGNED_OPTIONS = [
  { id: "Yes", value: "Yes" },
  { id: "No", value: "No" },
];
export const POPULATION_GROUPS = [
  { key: "African", label: "African", category: "black" },
  { key: "Coloured", label: "Coloured", category: "black" },
  { key: "Indian", label: "Indian", category: "black"},
  { key: "White", label: "White", category: "other"},
  { key: "Foreigner", label: "Foreigner", category: "other"},
];

export const EAP_REGIONS = [
  { name: "National", isNational: true },
  { name: "Eastern Cape", isNational: false },
  { name: "Free State", isNational: false },
  { name: "Gauteng", isNational: false },
  { name: "KwaZulu-Natal", isNational: false },
  { name: "Limpopo", isNational: false },
  { name: "Mpumalanga", isNational: false },
  { name: "North West", isNational: false },
  { name: "Northern Cape", isNational: false },
];

export const PROGRAM_DURATION_OPTIONS = [
  { id: "short_program", value: "Short Program" },
  { id: "qualification", value: "Qualification" },
];

export const BBBEE_LEVELS = [
  { key: "Level 1", value: "Level 1" },
  { key: "Level 2", value: "Level 2" },
  { key: "Level 3", value: "Level 3" },
  { key: "Level 4", value: "Level 4" },
  { key: "Level 5", value: "Level 5" },
  { key: "Level 6", value: "Level 6" },
  { key: "Level 7", value: "Level 7" },
  { key: "Level 8", value: "Level 8" },
  { key: "Non-compliant", value: "Non-compliant" }
]

export const SYSTEM_APPROACHES = [
  {
    key: "unstructured",
    value: "AI Generated",
    active: false  // Commented out for now
  },
  {
    key: "structured_without_training_selection",
    value: "Structured (Without Training Selection)",
    active: false
  },
  {
    key: "structured_with_training_selection",
    value: "Employee Selected",
    active: true
  },
];

/** Max number of trainings per skill row for unemployed learners (parameter-based). */
export const UNEMPLOYED_MAX_TRAININGS_PER_SKILL = 1;

export const FEATURE_FLAGS = {
  SHOW_COMPLETION_DATE: false,
  /** When false, Plan Optimizer is hidden from the sidebar menu. */
  SHOW_PLAN_OPTIMIZER_MENU: true,
};

export const MAX_DEPARTMENT_BUDGET = 100000000; // 100 million

// Default values for training allocation when trainings are selected
// These values are used both in the database trigger and frontend state
export const DEFAULT_TRAINING_ALLOCATION = {
  TRAINING_CATEGORY: "Category D",
  TRAINING_TYPE: "Learnership",
};